// Common prop type definitions and interfaces for consistent component APIs

// Status types used across the application
export const StatusTypes = {
  SUCCESS: 'success',
  ERROR: 'error', 
  WARNING: 'warning',
  INFO: 'info',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  DRAFT: 'draft'
};

// Severity levels for vulnerabilities, gaps, etc.
export const SeverityTypes = {
  CRITICAL: 'critical',
  HIGH: 'high', 
  MEDIUM: 'medium',
  LOW: 'low',
  INFO: 'info'
};

// Component size variants
export const SizeVariants = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'xlarge'
};

// Theme variants for styling
export const ThemeVariants = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  ACCENT: 'accent',
  NEUTRAL: 'neutral',
  ATHENA: 'athena',
  THEMIS: 'themis',
  ARES: 'ares'
};

// Loading states
export const LoadingStates = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

// Common prop shapes that components should implement
export const CommonPropShapes = {
  // Standard component props
  Component: {
    className: 'string',
    style: 'object',
    children: 'node',
    id: 'string',
    'data-testid': 'string'
  },
  
  // Interactive component props
  Interactive: {
    onClick: 'function',
    onFocus: 'function',
    onBlur: 'function',
    disabled: 'boolean',
    loading: 'boolean',
    'aria-label': 'string'
  },
  
  // Form component props
  FormControl: {
    name: 'string',
    value: 'any',
    onChange: 'function',
    onBlur: 'function',
    error: 'string',
    helperText: 'string',
    required: 'boolean',
    placeholder: 'string'
  },
  
  // Status display props
  Status: {
    status: 'oneOf',
    severity: 'oneOf',
    variant: 'oneOf',
    size: 'oneOf'
  },
  
  // Loading/async props
  AsyncComponent: {
    loading: 'boolean',
    error: 'string',
    onRetry: 'function',
    isRefreshing: 'boolean'
  },
  
  // Navigation props
  Navigation: {
    href: 'string',
    to: 'string',
    external: 'boolean',
    replace: 'boolean',
    state: 'object'
  },
  
  // Modal/Dialog props
  Modal: {
    open: 'boolean',
    onClose: 'function',
    onConfirm: 'function',
    onCancel: 'function',
    title: 'string',
    closable: 'boolean'
  }
};

// Validation helpers for prop types
export const PropValidators = {
  status: (props, propName, componentName) => {
    const value = props[propName];
    if (value && !Object.values(StatusTypes).includes(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of value \`${value}\` supplied to \`${componentName}\`, expected one of ${Object.values(StatusTypes).join(', ')}.`
      );
    }
  },
  
  severity: (props, propName, componentName) => {
    const value = props[propName];
    if (value && !Object.values(SeverityTypes).includes(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of value \`${value}\` supplied to \`${componentName}\`, expected one of ${Object.values(SeverityTypes).join(', ')}.`
      );
    }
  },
  
  size: (props, propName, componentName) => {
    const value = props[propName];
    if (value && !Object.values(SizeVariants).includes(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of value \`${value}\` supplied to \`${componentName}\`, expected one of ${Object.values(SizeVariants).join(', ')}.`
      );
    }
  }
};

// Common default props
export const CommonDefaults = {
  size: SizeVariants.MEDIUM,
  variant: ThemeVariants.PRIMARY,
  loading: false,
  disabled: false,
  closable: true
};